/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Button, breakpoints } from '@coursera/cds-core';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import ProfileBanner from 'bundles/account-profile/components/recruiting-profile-viewer/components/ProfileBanner';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/account-profile';

import { ProfileContext } from '../../ProfileContextProvider';

const styles = {
  ctaButton: () => css`
    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-200);
    }
  `,
};

type PropsFromCaller = {
  hasAtLeastOneProject?: boolean;
  onBrowseProjectsClick: (ev: React.MouseEvent<HTMLButtonElement>) => void;
};

const BrowseProjectsButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: (ev: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
  const { sharedEventingV3Data } = useContext(ProfileContext);
  const retrackedTrack = useRetracked();
  const track = useTracker();

  const visibilityTrackingRef: React.MutableRefObject<HTMLButtonElement | null> = useVisibilityTracker('view_button', {
    button: {
      name: EVENTS.profileBrowseProjects,
    },
    ...sharedEventingV3Data,
  });

  const handleClick = (ev: React.MouseEvent<HTMLButtonElement>) => {
    retrackedTrack({
      trackingName: EVENTS.profileBrowseProjects,
      trackingData: {},
      action: 'click',
    });

    track('click_button', {
      button: {
        name: EVENTS.profileBrowseProjects,
      },
      ...sharedEventingV3Data,
    });

    onClick(ev);
  };

  return (
    <TrackedDiv
      trackingName={EVENTS.profileBrowseProjects}
      withVisibilityTracking
      requireFullyVisible={false}
      trackClicks={false}
    >
      <Button ref={visibilityTrackingRef} onClick={handleClick} variant="ghost" size="small" css={styles.ctaButton}>
        {label}
      </Button>
    </TrackedDiv>
  );
};

const BrowseProjectsBanner = ({ hasAtLeastOneProject, onBrowseProjectsClick }: PropsFromCaller) => {
  const { restrictHiringSolutions } = useContext(ProfileContext);

  const getEmptyStateHeadingText = () => {
    return restrictHiringSolutions
      ? _t('Showcase your skills with job-relevant projects')
      : _t('Showcase your skills to recruiters with job-relevant projects');
  };

  if (hasAtLeastOneProject) {
    return (
      <ProfileBanner
        heading={_t('Make your profile stand out by adding multiple projects that showcase your skills')}
        body={_t(
          "You're off to a great start! To keep going, complete additional projects to demonstrate your skills in different areas. Showing 3 or more projects here is a great way to make sure your profile gets noticed."
        )}
        ctaContent={<BrowseProjectsButton label={_t('Browse Projects')} onClick={onBrowseProjectsClick} />}
      />
    );
  }

  return (
    <ProfileBanner
      heading={getEmptyStateHeadingText()}
      body={_t('Add projects here to demonstrate your technical expertise and ability to solve real-world problems.')}
      ctaContent={<BrowseProjectsButton label={_t('Browse Projects')} onClick={onBrowseProjectsClick} />}
    />
  );
};

export default BrowseProjectsBanner;
